import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Clock, Plus, Edit, Trash2, ExternalLink,
  Save, X, Ticket, Globe, Loader2
} from 'lucide-react';
import './Appearances.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Appearances = () => {
  const [appearances, setAppearances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    venue: '',
    city: '',
    date: '',
    time: '',
    description: '',
    external_link: '',
    ticket_link: '',
    image: ''
  });

  const getToken = () => localStorage.getItem('artistToken');

  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchAppearances();
  }, []);

  const fetchAppearances = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/appearances`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAppearances(data);
      }
    } catch (error) {
      console.error('Error fetching appearances:', error);
      toast.error('Failed to load appearances');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      venue: '',
      city: '',
      date: '',
      time: '',
      description: '',
      external_link: '',
      ticket_link: '',
      image: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (appearance) => {
    setFormData({
      title: appearance.title || '',
      venue: appearance.venue || '',
      city: appearance.city || '',
      date: appearance.date || '',
      time: appearance.time || '',
      description: appearance.description || '',
      external_link: appearance.external_link || '',
      ticket_link: appearance.ticket_link || '',
      image: appearance.image || ''
    });
    setEditingId(appearance.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const url = editingId 
        ? `${API_URL}/api/artist/appearances/${editingId}`
        : `${API_URL}/api/artist/appearances`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingId ? 'Appearance updated!' : 'Appearance added!');
        resetForm();
        fetchAppearances();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save appearance');
      }
    } catch (error) {
      console.error('Error saving appearance:', error);
      toast.error('Failed to save appearance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appearance?')) return;

    try {
      const res = await fetch(`${API_URL}/api/artist/appearances/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.ok) {
        toast.success('Appearance deleted');
        fetchAppearances();
      } else {
        toast.error('Failed to delete appearance');
      }
    } catch (error) {
      console.error('Error deleting appearance:', error);
      toast.error('Failed to delete appearance');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    try {
      const eventDate = new Date(dateStr);
      return eventDate >= new Date();
    } catch {
      return false;
    }
  };

  const upcomingAppearances = appearances.filter(a => isUpcoming(a.date));
  const pastAppearances = appearances.filter(a => !isUpcoming(a.date));

  if (loading) {
    return (
      <div className="appearances-page">
        <div className="loading-state">
          <Loader2 className="spinner" size={32} />
          <p>Loading appearances...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="appearances-page" data-testid="appearances-page">
      <header className="appearances-header">
        <div className="header-content">
          <h1>My Appearances</h1>
          <p>Manage your upcoming gigs and past performances</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          data-testid="add-appearance-btn"
        >
          <Plus size={18} />
          Add Appearance
        </button>
      </header>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="form-modal-overlay" onClick={() => resetForm()}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Appearance' : 'Add New Appearance'}</h2>
              <button className="close-btn" onClick={resetForm}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="appearance-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Event Name *</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Club Night at Warehouse"
                    required
                    data-testid="appearance-title"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="venue">Venue *</label>
                  <input
                    id="venue"
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({...formData, venue: e.target.value})}
                    placeholder="e.g., The Warehouse"
                    required
                    data-testid="appearance-venue"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="e.g., Toronto, ON"
                    required
                    data-testid="appearance-city"
                  />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="date">Date *</label>
                  <input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    data-testid="appearance-date"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="time">Time</label>
                  <input
                    id="time"
                    type="text"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    placeholder="e.g., 10:00 PM - 2:00 AM"
                    data-testid="appearance-time"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the event..."
                  rows={3}
                  data-testid="appearance-description"
                />
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label htmlFor="external_link">
                    <Globe size={14} /> Event Page URL
                  </label>
                  <input
                    id="external_link"
                    type="url"
                    value={formData.external_link}
                    onChange={(e) => setFormData({...formData, external_link: e.target.value})}
                    placeholder="https://..."
                    data-testid="appearance-external-link"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ticket_link">
                    <Ticket size={14} /> Ticket Link
                  </label>
                  <input
                    id="ticket_link"
                    type="url"
                    value={formData.ticket_link}
                    onChange={(e) => setFormData({...formData, ticket_link: e.target.value})}
                    placeholder="https://..."
                    data-testid="appearance-ticket-link"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="image">Event Image URL</label>
                <input
                  id="image"
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="https://... (optional)"
                  data-testid="appearance-image"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={submitting}
                  data-testid="save-appearance-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="spinner" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingId ? 'Update' : 'Add'} Appearance
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appearances List */}
      <div className="appearances-content">
        {appearances.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <h3>No appearances yet</h3>
            <p>Add your upcoming gigs and past performances to showcase on your profile and the AMR Events page.</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <Plus size={18} />
              Add Your First Appearance
            </button>
          </div>
        ) : (
          <>
            {/* Upcoming */}
            {upcomingAppearances.length > 0 && (
              <section className="appearances-section">
                <h2 className="section-title">
                  <Calendar size={20} />
                  Upcoming ({upcomingAppearances.length})
                </h2>
                <div className="appearances-grid">
                  {upcomingAppearances.map((appearance) => (
                    <div key={appearance.id} className="appearance-card upcoming" data-testid={`appearance-${appearance.id}`}>
                      {appearance.image && (
                        <div className="card-image">
                          <img src={appearance.image} alt={appearance.title} />
                        </div>
                      )}
                      <div className="card-content">
                        <h3>{appearance.title}</h3>
                        <div className="card-details">
                          <span className="detail">
                            <MapPin size={14} />
                            {appearance.venue}, {appearance.city}
                          </span>
                          <span className="detail">
                            <Calendar size={14} />
                            {formatDate(appearance.date)}
                          </span>
                          {appearance.time && (
                            <span className="detail">
                              <Clock size={14} />
                              {appearance.time}
                            </span>
                          )}
                        </div>
                        {appearance.description && (
                          <p className="card-description">{appearance.description}</p>
                        )}
                        <div className="card-links">
                          {appearance.external_link && (
                            <a href={appearance.external_link} target="_blank" rel="noopener noreferrer" className="link-btn">
                              <ExternalLink size={14} /> Event Page
                            </a>
                          )}
                          {appearance.ticket_link && (
                            <a href={appearance.ticket_link} target="_blank" rel="noopener noreferrer" className="link-btn ticket">
                              <Ticket size={14} /> Tickets
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="card-actions">
                        <button 
                          className="action-btn edit"
                          onClick={() => handleEdit(appearance)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDelete(appearance.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Past */}
            {pastAppearances.length > 0 && (
              <section className="appearances-section past">
                <h2 className="section-title">
                  <Clock size={20} />
                  Past ({pastAppearances.length})
                </h2>
                <div className="appearances-grid">
                  {pastAppearances.map((appearance) => (
                    <div key={appearance.id} className="appearance-card past" data-testid={`appearance-${appearance.id}`}>
                      {appearance.image && (
                        <div className="card-image">
                          <img src={appearance.image} alt={appearance.title} />
                        </div>
                      )}
                      <div className="card-content">
                        <h3>{appearance.title}</h3>
                        <div className="card-details">
                          <span className="detail">
                            <MapPin size={14} />
                            {appearance.venue}, {appearance.city}
                          </span>
                          <span className="detail">
                            <Calendar size={14} />
                            {formatDate(appearance.date)}
                          </span>
                        </div>
                      </div>
                      <div className="card-actions">
                        <button 
                          className="action-btn edit"
                          onClick={() => handleEdit(appearance)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDelete(appearance.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Appearances;
